/**
 * Split shell for the unauthenticated screens: the form on a dark panel at the
 * left, brand messaging over artwork at the right.
 *
 * The right panel is decorative and marketing copy only, so it is hidden below
 * `lg` rather than stacked — on a phone it would push the form off the first
 * screen, and it carries nothing a person needs in order to sign in.
 */
import type { ReactNode } from 'react';
import { BrandLockup, HeroScene } from '@/components/brand';

export function AuthLayout({
  title,
  description,
  children,
  footer,
  /** Rendered under the card, above the footer — e.g. the security assurance. */
  aside,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-svh bg-ink-950 text-slate-200">
      {/* ------------------------------ Form ------------------------------ */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-10 lg:w-[38%] lg:shrink-0 lg:px-14 xl:px-20">
        <div className="mx-auto w-full max-w-sm">
          <BrandLockup className="mb-12" />

          <h1 className="text-[2.1rem] font-semibold leading-tight tracking-tight text-white">
            {title}
          </h1>
          {description && (
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{description}</p>
          )}

          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            {children}
          </div>

          {aside && <div className="mt-6">{aside}</div>}

          {footer && (
            <>
              <hr className="mt-8 border-white/10" />
              <div className="mt-5 text-center text-sm text-slate-400">{footer}</div>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------ Hero ------------------------------ */}
      <div className="relative hidden flex-1 overflow-hidden lg:block">
        {/*
          The diagonal edge is a clip on the hero rather than a shape drawn over
          the form panel, so the form's own background stays a flat surface.
        */}
        <div className="absolute inset-0 [clip-path:polygon(9rem_0,100%_0,100%_100%,0_100%)]">
          <HeroScene className="size-full" />
        </div>

        <div className="relative flex h-full flex-col justify-center px-14 pl-40 xl:px-20 xl:pl-52">
          <h2 className="max-w-md text-[2.6rem] font-semibold leading-[1.12] tracking-tight text-white">
            Defensible intelligence.
            <br />
            Stronger decisions.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-300">
            MindPeer empowers advisors with deep market insight, rigorous analysis, and
            repeatable frameworks to win with confidence.
          </p>
        </div>
      </div>
    </div>
  );
}
